import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, Save, LogOut, RefreshCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PW_KEY = "admin_pw";

export default function Admin() {
  const [pw, setPw] = useState("");
  const [authed, setAuthed] = useState(false);
  const [remaining, setRemaining] = useState("");
  const [vipRemaining, setVipRemaining] = useState("");
  const [capacity, setCapacity] = useState(null);
  const [subs, setSubs] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (password) => {
    const r = await axios.get(`${API}/capacity`);
    setCapacity(r.data);
    setRemaining(String(r.data.remaining));
    setVipRemaining(String(r.data.vip_remaining ?? 19));
    try {
      const s = await axios.get(`${API}/admin/subscribers`, {
        headers: { "x-admin-password": password || pw },
      });
      setSubs(s.data.subscribers || []);
    } catch { /* keep old list */ }
    return r.data;
  };

  const downloadCsv = () => {
    const rows = ["email,date", ...subs.map((s) => `${s.email},${(s.created_at || "").slice(0, 19)}`)];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "waitlist-25-10.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  useEffect(() => {
    const stored = sessionStorage.getItem(PW_KEY);
    if (stored) {
      setBusy(true);
      axios
        .post(`${API}/admin/login`, {}, { headers: { "x-admin-password": stored } })
        .then(async () => {
          setPw(stored);
          setAuthed(true);
          await load();
        })
        .catch(() => sessionStorage.removeItem(PW_KEY))
        .finally(() => setBusy(false));
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await axios.post(`${API}/admin/login`, {}, { headers: { "x-admin-password": pw } });
      sessionStorage.setItem(PW_KEY, pw);
      setAuthed(true);
      await load();
    } catch (err) {
      setMsg("Грешна парола");
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const n = Math.max(0, parseInt(remaining || "0", 10));
      const vn = Math.max(0, parseInt(vipRemaining || "0", 10));
      const r = await axios.post(
        `${API}/admin/capacity`,
        { remaining: n, vip_remaining: vn },
        { headers: { "x-admin-password": pw } }
      );
      setCapacity(r.data);
      setRemaining(String(r.data.remaining));
      setVipRemaining(String(r.data.vip_remaining));
      setMsg("Запазено");
    } catch (err) {
      setMsg("Грешка при запазване");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PW_KEY);
    setAuthed(false);
    setPw("");
    setCapacity(null);
  };

  if (!authed) {
    return (
      <main data-testid="admin-login" className="min-h-[100svh] bg-[#0B1F3D] text-[#F5EBD9] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm bg-[#0F2647] border border-[#F5EBD9]/10 p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Lock className="w-5 h-5 text-[#F5892E]" strokeWidth={2} />
            <span className="font-mono text-xs uppercase tracking-[0.32em] text-[#F5EBD9]/70">
              Admin достъп
            </span>
          </div>
          <h1 className="font-display uppercase text-2xl mb-6">Играта на Имотите</h1>
          <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60 mb-2">
            Парола
          </label>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            required
            data-testid="admin-password-input"
            className="w-full px-4 py-3 bg-[#0B1F3D] border border-[#F5EBD9]/20 rounded-lg text-[#F5EBD9] focus:outline-none focus:border-[#F5892E] transition-colors mb-6"
          />
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-accent w-full px-6 py-3 rounded-full uppercase tracking-widest text-sm font-medium disabled:opacity-60"
          >
            {busy ? "Проверявам..." : "Влез"}
          </button>
          {msg && <div className="mt-4 text-sm text-[#F5892E] text-center">{msg}</div>}
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-panel" className="min-h-[100svh] bg-[#0B1F3D] text-[#F5EBD9] py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5892E] mb-1">
              Admin панел
            </div>
            <h1 className="font-display uppercase text-2xl sm:text-3xl">Управление на билети</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.24em] text-[#F5EBD9]/60 hover:text-[#F5892E] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={2} />
            Изход
          </button>
        </div>

        {capacity && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#0F2647] border border-[#F5EBD9]/10 p-5 rounded-lg">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/60 mb-1">
                Общ капацитет
              </div>
              <div className="font-display text-3xl text-[#F5EBD9]">{capacity.total}</div>
            </div>
            <div className="bg-[#0F2647] border border-[#F5EBD9]/10 p-5 rounded-lg">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/60 mb-1">
                Заети
              </div>
              <div className="font-display text-3xl text-[#F5892E]">{capacity.sold}</div>
            </div>
            <div className="bg-[#0F2647] border border-[#F5EBD9]/10 p-5 rounded-lg">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#F5EBD9]/60 mb-1">
                Остават
              </div>
              <div className="font-display text-3xl text-[#E0B95C]">{capacity.remaining}</div>
            </div>
            <div className="bg-[#0F2647] border border-[#E0B95C]/40 p-5 rounded-lg">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#E0B95C]/80 mb-1">
                VIP остават
              </div>
              <div className="font-display text-3xl text-[#E0B95C]">{capacity.vip_remaining ?? "—"}</div>
            </div>
          </div>
        )}

        <form onSubmit={save} className="bg-[#0F2647] border border-[#F5EBD9]/10 p-6 sm:p-8 rounded-lg">
          <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60 mb-3">
            Остатъчни билети (Remaining)
          </label>
          <input
            type="number"
            min="0"
            max={capacity ? capacity.total : 350}
            value={remaining}
            onChange={(e) => setRemaining(e.target.value)}
            data-testid="admin-remaining-input"
            className="w-full px-4 py-3 bg-[#0B1F3D] border border-[#F5EBD9]/20 rounded-lg text-[#F5EBD9] text-2xl font-display focus:outline-none focus:border-[#F5892E] transition-colors mb-4"
          />
          <label className="block font-mono text-[10px] uppercase tracking-[0.32em] text-[#E0B95C]/80 mb-3">
            Остатъчни VIP билети (VIP Remaining)
          </label>
          <input
            type="number"
            min="0"
            max={capacity ? (capacity.vip_total || 50) : 50}
            value={vipRemaining}
            onChange={(e) => setVipRemaining(e.target.value)}
            data-testid="admin-vip-remaining-input"
            className="w-full px-4 py-3 bg-[#0B1F3D] border border-[#E0B95C]/30 rounded-lg text-[#E0B95C] text-2xl font-display focus:outline-none focus:border-[#E0B95C] transition-colors mb-4"
          />
          <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#F5EBD9]/50 mb-6">
            След запазване стойността се обновява навсякъде на сайта в реално време.
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-save"
              className="btn-accent flex-1 px-6 py-3 rounded-full uppercase tracking-widest text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Save className="w-4 h-4" strokeWidth={2.5} />
              {busy ? "Записвам..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => load()}
              className="px-4 py-3 rounded-full border border-[#F5EBD9]/25 text-[#F5EBD9]/70 hover:text-[#F5EBD9] hover:border-[#F5EBD9]/60 transition-colors"
              aria-label="Reload"
            >
              <RefreshCcw className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          {msg && <div className="mt-4 text-sm text-[#F5892E]">{msg}</div>}
        </form>

        {/* Waitlist emails */}
        <div className="mt-8 bg-[#0F2647] border border-[#F5EBD9]/10 p-6 sm:p-8 rounded-lg" data-testid="admin-subscribers">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#F5EBD9]/60 mb-1">
                Записани имейли (лист на изчакващите · 25.10)
              </div>
              <div className="font-display text-3xl text-[#F5EBD9]" data-testid="admin-subscribers-count">
                {subs.length}
              </div>
            </div>
            <button
              type="button"
              onClick={downloadCsv}
              data-testid="admin-subscribers-csv"
              className="px-5 py-3 rounded-full bg-[#F5892E] text-[#0B1F3D] uppercase tracking-widest text-xs font-bold hover:bg-[#F5EBD9] transition-colors"
            >
              Свали CSV
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-[#F5EBD9]/10">
            {subs.length === 0 && (
              <div className="py-4 text-sm text-[#F5EBD9]/50">Все още няма записани имейли.</div>
            )}
            {subs.map((s) => (
              <div key={s.email} className="py-2.5 flex items-center justify-between gap-4 text-sm">
                <span className="text-[#F5EBD9]">{s.email}</span>
                <span className="font-mono text-[10px] text-[#F5EBD9]/40">{(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-[#F5EBD9]/40">
            С Brevo ключ в .env тези имейли ще се синхронизират автоматично.
          </div>
        </div>
      </div>
    </main>
  );
}
